/**
 * ReviewDetail — View / Edit a Review (detail mode only)
 *
 * Uses DetailFrame (like MatterDetails) with tabs:
 *   - Details: metadata + playbook import
 *   - Clause Rules: manage clause-level rules
 *   - Global Rules: manage cross-cutting rules
 *   - Sessions: start reviews + view past sessions
 *
 * Create mode is now handled by CreateReviewForm.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import RecentActivityCard from '../shared/RecentActivityCard';
import { fetchEntityAuditLog } from '../../store/actions/AuditLogActions';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import DetailFrame from '../shared/Frames/DetailFrame';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import TagInput from '../shared/TailwindComponents/TagInput';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import LogoSpinner from '../shared/LogoSpinner';
import {
  PiFloppyDiskLight,
  PiTrashLight,
  PiPlayLight,
  PiListBulletsLight,
  PiGlobeLight,
  PiClockCounterClockwiseLight,
  PiInfoLight,
  PiFileTextLight,
  PiSpinnerGapLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiQuestionLight,
  PiEyeLight,
  PiArrowsClockwiseLight,
  PiDownloadSimpleLight,
  PiUsersThreeLight,
  PiArrowLeftLight,
  PiScalesLight,
  PiCopyLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import {
  fetchReviewDetail,
  updateReview,
  deleteReview,
  extractPlaybook,
  streamPlaybookSourceDocument,
  REVIEW_EXTRACTING_PLAYBOOK,
  addPlaybookCollaborator,
  updatePlaybookCollaborator,
  removePlaybookCollaborator,
  createReview,
} from '../../store/actions/Review/ReviewActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';

import ReviewClauseRules from './ReviewClauseRules';
import ReviewGlobalRules from './ReviewGlobalRules';
import ReviewSessions from './ReviewSessions';
import CreateReviewForm from './CreateReviewForm';
import DeleteTemplateConfirmation from '../Templates/DeleteTemplateConfirmation';
import { usePlaybookExtractionPubSub } from './usePlaybookExtractionPubSub';
import { extractHtmlFromDocx } from '../../utils/docxHandler';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import { ExtractionStep } from '../../types/review';
import { PlaybookSharedUser } from '../../types/playbook';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, playbookDetailTourSteps } from '../shared/FeatureTour';

// ─── Tab config ──────────────────────────────────────────────────────────────

type TabKey = 'details' | 'clause_rules' | 'global_rules' | 'sessions';

interface Tab {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  count?: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

const ReviewDetail: React.FC = () => {
  const { playbookId } = useParams<{ playbookId: string }>();
  const isCreateMode = playbookId === 'new';

  // Render create form for /playbooks/new
  if (isCreateMode) {
    return <CreateReviewForm />;
  }

  return <ReviewDetailInner reviewId={playbookId} />;
};

const ReviewDetailInner: React.FC<{ reviewId: string }> = ({ reviewId }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  // Redux state
  const userInfo = useSelector((s: RootState) => s.user.userInfo);
  const currentReview = useSelector((s: RootState) => s.review.currentReview);
  const isLoadingDetail = useSelector((s: RootState) => s.review.isLoadingDetail);
  const isExtractingPlaybook = useSelector((s: RootState) => s.review.isExtractingPlaybook);
  const extractionProgress = useSelector((s: RootState) => s.review.extractionProgress);

  // Bidirectional sync: keep isExtractingPlaybook in line with the backend extraction_status.
  // ▸ If server says 'processing' but flag is off → activate it (reconnects PubSub)
  // ▸ If server says done/completed/failed but flag is still on → clear it (fixes stale state after navigation)
  useEffect(() => {
    if (!currentReview) return;
    const status = currentReview.extraction_status;

    if (status === 'processing' && !isExtractingPlaybook && !extractionProgress.complete) {
      dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: true });
    } else if (status && status !== 'processing' && isExtractingPlaybook) {
      dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });
    }
  }, [currentReview, currentReview?.extraction_status, isExtractingPlaybook, extractionProgress.complete, dispatch]);

  // PubSub: listen for extraction progress events
  usePlaybookExtractionPubSub(reviewId, isExtractingPlaybook);

  // Derived: is the UI locked because extraction is running?
  const isProcessing = isExtractingPlaybook;

  // Is this a PONS-owned global playbook? → fully read-only
  const isGlobal = !!currentReview?.is_global;

  // Local state
  const [activeTab, setActiveTab] = useState<TabKey>('details');

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'playbook-detail',
    steps: playbookDetailTourSteps,
    readyToShow: !!currentReview && !isLoadingDetail,
    autoStartDelay: 600,
  });

  // Force details tab while extracting so user sees progress
  useEffect(() => {
    if (isProcessing && activeTab !== 'details') {
      setActiveTab('details');
    }
  }, [isProcessing]); // eslint-disable-line react-hooks/exhaustive-deps
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agreementTypes, setAgreementTypes] = useState<string[]>([]);
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [auditTarget, setAuditTarget] = useState<{type: string; id: string; name: string} | null>(null);

  // Playbook state
  const [playbookFiles, setPlaybookFiles] = useState<File[]>([]);
  const [isStreamingSource, setIsStreamingSource] = useState(false);

  // ─── Data loading ──────────────────────────────────────────────────────

  useEffect(() => {
    if (reviewId) {
      dispatch(fetchReviewDetail(reviewId));
    }
  }, [dispatch, reviewId]);

  useEffect(() => {
    if (currentReview) {
      setName(currentReview.name || '');
      setDescription(currentReview.description || '');
      setAgreementTypes(currentReview.agreement_types || []);
      setJurisdictions(currentReview.jurisdictions || []);
    }
  }, [currentReview]);

  // ─── Save ──────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!currentReview || !name.trim()) return;
    setIsSaving(true);
    try {
      await dispatch(
        updateReview(currentReview.uuid, {
          name: name.trim(),
          description,
          agreement_types: agreementTypes,
          jurisdictions,
        }) as any,
      );
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, currentReview, name, description, agreementTypes, jurisdictions]);

  // ─── Delete ────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    if (!currentReview) return;
    dispatch(
      showModal({
        type: 'custom' as any,
        message: t('Are you sure you want to delete this playbook?'),
        icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
        width: '500px',
        showModal: true,
        content: (
          <DeleteTemplateConfirmation
            count={1}
            entityLabel={t('playbook')}
            description={t('This will permanently delete this playbook, including all clause rules and global rules. This action cannot be undone.')}
            showDeleteSessions={true}
            onConfirm={async (deleteSessions?: boolean) => {
              const success = await dispatch(deleteReview(currentReview.uuid, deleteSessions || false) as any);
              if (success) {
                dispatch(hideModal());
                history.push('/templates?type=playbooks');
              }
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
      }),
    );
  }, [dispatch, currentReview, history, t]);

  // ─── Playbook extraction ───────────────────────────────────────────────


  const handleExtractPlaybook = useCallback(async () => {
    if (!currentReview || playbookFiles.length === 0) return;
    const result = await dispatch(extractPlaybook(currentReview.uuid, playbookFiles[0]) as any);
    if (result) {
      setPlaybookFiles([]);
      // PubSub hook will auto-refetch detail on completion
    }
  }, [dispatch, currentReview, playbookFiles]);

  // ─── View / Download source document ────────────────────────────────────

  const handleViewSourceDocument = useCallback(async () => {
    if (!currentReview) return;
    setIsStreamingSource(true);
    try {
      const result = await dispatch(streamPlaybookSourceDocument(currentReview.uuid, 'inline') as any);
      if (result?.success && result.blob) {
        const ct = (result.contentType || '').toLowerCase();
        const docName = currentReview.source_document_name || 'Playbook Document';
        const ext = docName.split('.').pop()?.toLowerCase() || '';
        const isPdf = ct.includes('pdf') || ext === 'pdf';
        const isDocx = ext === 'docx' || ct.includes('officedocument') || ct.includes('msword');

        if (isDocx) {
          // Convert DOCX to HTML before opening side panel
          try {
            const arrayBuffer = await (result.blob as Blob).arrayBuffer();
            const html = await extractHtmlFromDocx(arrayBuffer);
            if (html) {
              const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; line-height: 1.6; color: #1f2937; background: #fff; }
  table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  td, th { border: 1px solid #d1d5db; padding: 8px; }
  img { max-width: 100%; height: auto; }
</style></head><body>${html}</body></html>`;
              dispatch(showSidePanel({
                type: 'fileReaderTst',
                showSidePanel: true,
                width: '70%',
                content: fullHtml,
                filename: docName,
                loading: false,
                message: docName,
                subMsg: t('Source Document Preview'),
              }));
            }
          } catch (convErr) {
            console.error('DOCX conversion failed:', convErr);
            // Fallback: pass blob URL, CustomFileReader will handle it
            const blobUrl = URL.createObjectURL(result.blob);
            dispatch(showSidePanel({
              type: 'fileReaderTst',
              showSidePanel: true,
              width: '70%',
              fileUrl: blobUrl,
              filename: docName,
              loading: false,
              message: docName,
              subMsg: t('Source Document Preview'),
            }));
          }
        } else {
          // PDF and other formats: pass blob URL directly
          const blobUrl = URL.createObjectURL(result.blob);
          dispatch(showSidePanel({
            type: 'fileReaderTst',
            showSidePanel: true,
            width: '70%',
            fileUrl: blobUrl,
            isPdf,
            filename: docName,
            loading: false,
            message: docName,
            subMsg: t('Source Document Preview'),
          }));
        }
      }
    } finally {
      setIsStreamingSource(false);
    }
  }, [dispatch, currentReview, t]);

  const handleDownloadSourceDocument = useCallback(async () => {
    if (!currentReview) return;
    const result = await dispatch(streamPlaybookSourceDocument(currentReview.uuid, 'attachment') as any);
    if (result?.success && result.blob) {
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentReview.source_document_name || 'playbook_document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [dispatch, currentReview]);

  // ─── Upload new playbook (replace) ──────────────────────────────────────

  const handleUploadNewPlaybook = useCallback(() => {
    if (!currentReview) return;
    dispatch(showModal({
      type: 'replacePlaybookDocument' as any,
      message: t('Replace Source Document'),
      subMsg: t('Upload a new document to replace the current one. AI extraction will re-run automatically to update all rules.'),
      icon: <PiArrowsClockwiseLight className="w-5 h-5 text-blue-600" />,
      showModal: true,
      maxFiles: 1,
      allowedExtensions: ['pdf', 'docx', 'doc', 'txt'],
      onFileSelected: (file: File) => {
        dispatch(extractPlaybook(currentReview.uuid, file) as any);
      },
    }));
  }, [dispatch, currentReview, t]);

  // ─── Help guide modal ──────────────────────────────────────────────────

  const handleOpenHelp = useCallback(() => {
    dispatch(showModal({
      showModal: true,
      type: 'playbookHelp' as any,
      message: t('Understanding Playbooks'),
      icon: <PiQuestionLight className="w-5 h-5 text-gray-500" />,
    }));
  }, [dispatch, t]);

  // ─── Clone global playbook ─────────────────────────────────────────────

  const [isCloning, setIsCloning] = useState(false);

  const handleClonePlaybook = useCallback(async () => {
    if (!currentReview) return;
    setIsCloning(true);
    try {
      const result = await dispatch(
        createReview({
          name: `${currentReview.name} (${t('Copy')})`,
          description: currentReview.description || '',
          agreement_types: currentReview.agreement_types || [],
          jurisdictions: currentReview.jurisdictions || [],
          clause_rules: currentReview.clause_rules || [],
          global_rules: currentReview.global_rules || [],
        }) as any,
      );
      if (result?.uuid) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Playbook cloned successfully'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        history.push(`/playbooks/${result.uuid}`);
      }
    } catch {
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to clone playbook'),
          modalType: 'error',
          duration: 3000,
        }),
      );
    } finally {
      setIsCloning(false);
    }
  }, [dispatch, currentReview, history, t]);

  // ─── Manage Collaborators ──────────────────────────────────────────────

  const handleManageCollaborators = useCallback(() => {
    if (!currentReview) return;
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      width: '50%',
      message: t('Manage Access'),
      resourceUuid: currentReview.uuid,
      resourceType: 'review',
      collaborators: currentReview.has_access || [],
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addPlaybookCollaborator(uuid, { collaborator_id: userUuid, access: access as any }) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removePlaybookCollaborator(uuid, collabUuid) as any);
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updatePlaybookCollaborator(uuid, collabUuid, { access: access as any }) as any);
      },
      onUpdate: () => {
        dispatch(fetchReviewDetail(currentReview.uuid));
      },
    }));
  }, [dispatch, currentReview, t, userInfo?.UserUID]);

  // ─── Breadcrumbs ───────────────────────────────────────────────────────

  const breadcrumbs = useMemo(() => [
    { name: t('Templates'), href: '/templates?type=playbooks', current: false },
    { name: currentReview?.name || t('Loading...'), href: `/playbooks/${reviewId}`, current: true },
  ], [currentReview, reviewId, t]);

  // ─── Tabs ──────────────────────────────────────────────────────────────

  const tabs: Tab[] = useMemo(() => [
    { key: 'details', label: t('Details'), icon: <PiInfoLight className="w-4 h-4" /> },
    { key: 'clause_rules', label: t('Clause Rules'), icon: <PiListBulletsLight className="w-4 h-4" />, count: currentReview?.clause_rules?.length },
    { key: 'global_rules', label: t('Global Rules'), icon: <PiGlobeLight className="w-4 h-4" />, count: currentReview?.global_rules?.length },
    { key: 'sessions', label: t('Reviews'), icon: <PiClockCounterClockwiseLight className="w-4 h-4" />, count: currentReview?.recent_reviews?.length },
  ], [currentReview, t]);

  // ─── Header actions ────────────────────────────────────────────────────

  const headerActions = useMemo(() => {
    if (activeTab !== 'details' || isGlobal) return [];
    return [
      {
        label: t('Save Changes'),
        icon: <PiFloppyDiskLight className="w-4 h-4" />,
        onClick: handleSave,
        disabled: !name.trim() || isSaving || isProcessing,
      },
    ];
  }, [activeTab, handleSave, name, isSaving, isGlobal, t]);

  const dropdownActions = useMemo(() => {
    if (isGlobal) return [];
    return [
      {
        label: t('Manage Access'),
        icon: <PiUsersThreeLight className="w-4 h-4" />,
        onClick: handleManageCollaborators,
        disabled: isProcessing,
      },
      {
        label: t('Activity'),
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
        onClick: () => setAuditTarget({ type: 'review', id: reviewId, name: currentReview?.name || '' }),
        disabled: isProcessing,
      },
      {
        label: t('Delete Playbook'),
        icon: <PiTrashLight className="w-4 h-4" />,
        onClick: handleDelete,
        isDestructive: true,
        disabled: isProcessing,
      },
    ];
  }, [handleManageCollaborators, handleDelete, isProcessing, isGlobal, t, reviewId, currentReview?.name]);

  // ─── Loading state ─────────────────────────────────────────────────────

  if (isLoadingDetail && !currentReview) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} actions={[]}>
        <div className="flex items-center justify-center h-64">
          <LogoSpinner size="medium" />
        </div>
      </DetailFrame>
    );
  }

  if (!currentReview) {
    return <DetailFrame breadcrumbs={breadcrumbs} actions={[]} notFound><div /></DetailFrame>;
  }

  // ─── Collaborators for header ──────────────────────────────────────────

  const collaborators = (currentReview.has_access || []).map((u: PlaybookSharedUser) => ({
    user_uuid: String(u.user_id),
    name: u.name,
    initials: u.initials,
    access: u.access || 'view',
    email: u.email || null,
    avatar_url: u.profile_picture || null,
  }));

  return (<>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={headerActions}
      dropdownActions={dropdownActions}
      collaborators={isGlobal ? [] : collaborators}
      additionalHeaderElements={
        <>
          <ProjectBadges resourceType="review" resourceUuid={reviewId} />
          <PendingAccessRequests resourceType="review" resourceUuid={reviewId} />
          <Button
            onClick={handleOpenHelp}
            title={t('What is a Playbook?')}
            size='small'
            variant='outline'
            className='py-1.5'
          >
            <PiQuestionLight className="w-3.5 h-3.5" />
          </Button>
        </>
      }
    >
      <div className="flex-1 flex flex-col min-h-0">
      {/* Back button — only for PONS global playbooks */}
      {isGlobal && (
        <div className="px-4 pt-3 pb-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => history.push('/templates?type=playbooks')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <PiArrowLeftLight className="w-4 h-4" />
            {t('Back to Playbooks')}
          </button>
        </div>
      )}
      {/* Global playbook banner */}
      {isGlobal && (
        <div className="mx-4 mt-2 flex items-center justify-between gap-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <PiInfoLight className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
            <span className="text-sm text-blue-800 dark:text-blue-200">
              {t('This is a PONS-managed playbook. Rules are read-only.')}
            </span>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={handleClonePlaybook}
            icon={<PiCopyLight className="w-4 h-4" />}
            disabled={isCloning}
            isLoading={isCloning}
          >
            {t('Clone to My Playbooks')}
          </Button>
        </div>
      )}

      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0 sticky top-0 z-10 bg-white dark:bg-gray-900" data-tour-id="playbook-tab-bar">
        <nav className="flex space-x-2">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            const isDisabled = isProcessing && tab.key !== 'details';
            return (
              <button
                key={tab.key}
                onClick={() => !isDisabled && setActiveTab(tab.key)}
                disabled={isDisabled}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded transition-all duration-200 border',
                  isActive
                    ? 'text-primary-600 dark:text-primary-300 bg-white dark:bg-black border-gray-300'
                    : 'text-gray-600 dark:text-gray-300 border-transparent hover:border-gray-300 hover:bg-white dark:hover:bg-black',
                  isDisabled && 'opacity-50 cursor-not-allowed',
                )}
              >
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <Badge color="gray">{tab.count}</Badge>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main" style={{ maxHeight: 'calc(100vh - 100px)' }}>
        {/* Details tab */}
        {activeTab === 'details' && (
          <div className="p-6 space-y-6 animate-fade-in">
            {/* Stats Bar */}
            {(() => {
              const clauseRules = currentReview.clause_rules || [];
              const globalRules = currentReview.global_rules || [];
              const sessions = currentReview.recent_reviews || [];
              const avgRisk = clauseRules.length > 0
                ? (clauseRules.reduce((sum, r) => sum + (r.risk_weight || 0), 0) / clauseRules.length).toFixed(1)
                : '—';
              const lowCount = clauseRules.filter((r) => r.risk_weight >= 1 && r.risk_weight <= 3).length;
              const medCount = clauseRules.filter((r) => r.risk_weight >= 4 && r.risk_weight <= 7).length;
              const highCount = clauseRules.filter((r) => r.risk_weight >= 8 && r.risk_weight <= 10).length;
              const totalRisk = lowCount + medCount + highCount;

              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="grid grid-cols-4 divide-x divide-gray-200 dark:divide-gray-700">
                    <div className="p-4 flex items-center gap-3">
                      <PiListBulletsLight className="w-5 h-5 text-blue-500 shrink-0" />
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{clauseRules.length}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('Clause Rules')}</p>
                      </div>
                    </div>
                    <div className="p-4 flex items-center gap-3">
                      <PiGlobeLight className="w-5 h-5 text-purple-500 shrink-0" />
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{globalRules.length}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('Global Rules')}</p>
                      </div>
                    </div>
                    <div className="p-4 flex items-center gap-3">
                      <PiClockCounterClockwiseLight className="w-5 h-5 text-gray-500 shrink-0" />
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{sessions.length}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('Review Sessions')}</p>
                      </div>
                    </div>
                    <div className="p-4 flex items-center gap-3">
                      <PiScalesLight className="w-5 h-5 text-amber-500 shrink-0" />
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{avgRisk}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('Avg Risk Weight')}</p>
                      </div>
                    </div>
                  </div>
                  {totalRisk > 0 && (
                    <div className="px-4 pb-3">
                      <div className="flex h-1.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
                        {lowCount > 0 && (
                          <div
                            className="bg-emerald-500 transition-all"
                            style={{ width: `${(lowCount / totalRisk) * 100}%` }}
                            title={`${t('Low')}: ${lowCount}`}
                          />
                        )}
                        {medCount > 0 && (
                          <div
                            className="bg-amber-400 transition-all"
                            style={{ width: `${(medCount / totalRisk) * 100}%` }}
                            title={`${t('Medium')}: ${medCount}`}
                          />
                        )}
                        {highCount > 0 && (
                          <div
                            className="bg-red-500 transition-all"
                            style={{ width: `${(highCount / totalRisk) * 100}%` }}
                            title={`${t('High')}: ${highCount}`}
                          />
                        )}
                      </div>
                      <div className="flex gap-3 mt-1.5">
                        <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />{t('Low')} ({lowCount})</span>
                        <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />{t('Medium')} ({medCount})</span>
                        <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{t('High')} ({highCount})</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Two-column grid: Metadata (left) + Source Document (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left column: Playbook info + Categories */}
            <div className="lg:col-span-2 space-y-6">

            {/* Card 1: Playbook Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5" data-tour-id="playbook-details-section">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4">
                {t('Playbook Information')}
              </h4>
              <div className="space-y-4">
              {isGlobal ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Playbook Name')}</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{name || '—'}</p>
                  </div>
                  {description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Description')}</label>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{description}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Input
                    type="text"
                    id="review-name"
                    label={t('Playbook Name')}
                    placeholder={t('e.g. NDA Review Standard')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isProcessing}
                  />
                  <Textarea
                    id="review-description"
                    label={t('Description')}
                    placeholder={t('Describe what this review checks for...')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    disabled={isProcessing}
                  />
                </>
              )}
              </div>
            </div>

            {/* Card 2: Categories */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4">
                {t('Categories')}
              </h4>
              {isGlobal ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {agreementTypes.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Agreement Types')}</label>
                      <div className="flex flex-wrap gap-1">
                        {agreementTypes.map((at, i) => <Badge key={i} color="blue" className="capitalize">{at}</Badge>)}
                      </div>
                    </div>
                  )}
                  {jurisdictions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Jurisdictions')}</label>
                      <div className="flex flex-wrap gap-1">
                        {jurisdictions.map((j, i) => <Badge key={i} color="purple" className="capitalize">{j}</Badge>)}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TagInput
                    id="agreement-types"
                    label={t('Agreement Types')}
                    placeholder={t('Type and press Enter (e.g. NDA, SPA, Employment)')}
                    tags={agreementTypes}
                    onChange={setAgreementTypes}
                    disabled={isProcessing}
                  />
                  <TagInput
                    id="jurisdictions"
                    label={t('Jurisdictions')}
                    placeholder={t('Type and press Enter (e.g. Norway, EU, US-NY)')}
                    tags={jurisdictions}
                    onChange={setJurisdictions}
                    disabled={isProcessing}
                  />
                </div>
              )}
            </div>

            </div>{/* end left column */}

            {/* Right column: Source Document */}
            <div className="lg:col-span-1 space-y-4">
            {!isGlobal && <div className="space-y-3 sticky top-16" data-tour-id="playbook-source-document">
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                {t('Source Document')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('Upload your firm\'s playbook or standards document. PONS will automatically extract clause rules and global rules.')}
              </p>

              {/* Source document card (if previously uploaded) */}
              {currentReview.source_document_name && !isExtractingPlaybook && (
                <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                      <PiFileTextLight className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {currentReview.source_document_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('Source playbook document')}
                      </p>
                    </div>
                    <Badge color="green">{t('Imported')}</Badge>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <Button
                      variant="outline"
                      size="small"
                      onClick={handleViewSourceDocument}
                      icon={<PiEyeLight className="w-4 h-4" />}
                      disabled={isStreamingSource}
                      isLoading={isStreamingSource}
                    >
                      {t('View')}
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={handleDownloadSourceDocument}
                      icon={<PiDownloadSimpleLight className="w-4 h-4" />}
                    >
                      {t('Download')}
                    </Button>
                    <div className="flex-1" />
                    <Button
                      variant="outline"
                      size="small"
                      onClick={handleUploadNewPlaybook}
                      icon={<PiArrowsClockwiseLight className="w-4 h-4" />}
                    >
                      {t('Replace')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Extraction progress */}
              {isExtractingPlaybook && (
                <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                      <PiSpinnerGapLight className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200 block">
                        {t('Extracting rules from playbook...')}
                      </span>
                      <span className="text-xs text-blue-600 dark:text-blue-400">
                        {t('Editing is disabled while extraction is in progress')}
                      </span>
                    </div>
                  </div>
                  {extractionProgress.steps.length > 0 && (
                    <div className="space-y-2 pl-1">
                      {extractionProgress.steps.map((step: ExtractionStep) => (
                        <div key={step.id} className="flex items-center gap-2 text-xs">
                          {step.status === 'completed' ? (
                            <PiCheckCircleLight className="w-4 h-4 text-green-500 shrink-0" />
                          ) : step.status === 'failed' ? (
                            <PiWarningCircleLight className="w-4 h-4 text-red-500 shrink-0" />
                          ) : step.status === 'in_progress' ? (
                            <PiSpinnerGapLight className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 shrink-0" />
                          )}
                          <span className={clsx(
                            'truncate',
                            step.status === 'completed' ? 'text-green-700 dark:text-green-400' :
                            step.status === 'in_progress' ? 'text-blue-700 dark:text-blue-300' :
                            'text-gray-500 dark:text-gray-400'
                          )}>
                            {step.message || step.name || step.id}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {extractionProgress.steps.length === 0 && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 pl-1">
                      {t('Connecting to processing pipeline...')}
                    </p>
                  )}
                </div>
              )}

              {/* Extraction completed message */}
              {extractionProgress.complete && !isExtractingPlaybook && (
                <div className={clsx(
                  'p-3 rounded-lg border flex items-center gap-2',
                  extractionProgress.ok
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                )}>
                  {extractionProgress.ok ? (
                    <PiCheckCircleLight className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <PiWarningCircleLight className="w-5 h-5 text-red-600 shrink-0" />
                  )}
                  <span className={clsx(
                    'text-sm',
                    extractionProgress.ok ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                  )}>
                    {extractionProgress.summary || (extractionProgress.ok ? t('Extraction complete!') : t('Extraction failed'))}
                  </span>
                </div>
              )}

              {/* Upload area — shown only when no source doc exists */}
              {!currentReview.source_document_name && !isExtractingPlaybook && (
                <div className="space-y-3">
                  <FileUploader
                    files={playbookFiles}
                    onFilesChange={setPlaybookFiles}
                    allowedExtensions={['pdf', 'docx', 'doc', 'txt']}
                    maxFiles={1}
                  />
                  {playbookFiles.length > 0 && (
                    <div className="flex justify-end">
                      <Button
                        variant="primary"
                        onClick={handleExtractPlaybook}
                        size='small'
                        icon={<PiPlayLight className="w-4 h-4" />}
                        disabled={isExtractingPlaybook}
                        isLoading={isExtractingPlaybook}
                      >
                        {t('Upload & Extract Rules')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>}

              {/* Recent activity (right column, below Source Document) */}
              <RecentActivityCard
                fetchEntries={async (limit) => {
                  const res: any = await dispatch(fetchEntityAuditLog('Playbook', reviewId, 1, limit) as any);
                  return res?.data || [];
                }}
              />
            </div>{/* end right column */}
            </div>{/* end grid */}
          </div>
        )}

        {/* Clause Rules tab */}
        {activeTab === 'clause_rules' && (
          <ReviewClauseRules
            reviewUuid={currentReview.uuid}
            clauseRules={currentReview.clause_rules || []}
            readOnly={isGlobal}
          />
        )}

        {/* Global Rules tab */}
        {activeTab === 'global_rules' && (
          <ReviewGlobalRules
            reviewUuid={currentReview.uuid}
            globalRules={currentReview.global_rules || []}
            readOnly={isGlobal}
          />
        )}

        {/* Sessions tab */}
        {activeTab === 'sessions' && (
          <ReviewSessions
            reviewUuid={currentReview.uuid}
            sessions={currentReview.recent_reviews || []}
          />
        )}
      </div>
      </div>
    </DetailFrame>
    {auditTarget && (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-20 z-40" onClick={() => setAuditTarget(null)} />
        <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 shadow-xl z-50 animate-slide-in-right">
          <AuditLogPanel
            entityType={auditTarget.type}
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </div>
      </>
    )}

    <FeatureTour
      steps={playbookDetailTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
  </>);
};

export default ReviewDetail;
